import { AxiosRequestConfig } from 'axios';
import t from 'modules/i18n/intl';
import { State } from 'model/State';
import hamsClient from 'modules/hams';
import { HamsCart } from 'modules/cart/duck/model';
import FormValidationError from 'model/hams/FormValidationError';
import { reportErrorOnly } from 'util/errorReporter';
import { setInteractionError } from '@atlassian/react-ufo/set-interaction-error';
import { CHECKOUT_CONTACTS_SUBMIT_UFO_NAME } from 'util/ufoNameConstants';
import {
  checkIsCountryChile,
  checkIsCountryEgypt,
  checkIsCountryPhilippines,
  checkIsCountrySerbia,
  checkIsCountryThailand,
  checkIsCountryUkraine,
  checkIsCountryUzbekistan,
} from 'modules/utils';

const getRequiredFieldsErrors = (cart: Partial<HamsCart>, getState: () => State) => {
  const reduxState = getState();
  const contactSectionsRequiredFields = ['email', 'firstName', 'lastName', 'phone'];

  const organisationSectionsRequiredFields = (
    isoCountryCode: string | undefined,
    taxId: string | undefined,
  ) => {
    const country = isoCountryCode
      ? reduxState.data.countries.details[isoCountryCode]?.result
      : undefined;

    return [
      'address1',
      'city',
      'isoCountryCode',
      'organisationName',
      country?.requiresPostcode && 'postcode',
      country?.requiresStates && 'state',
      (checkIsCountryChile(isoCountryCode) ||
        checkIsCountryPhilippines(isoCountryCode) ||
        checkIsCountryThailand(isoCountryCode) ||
        checkIsCountrySerbia(isoCountryCode) ||
        checkIsCountryUzbekistan(isoCountryCode)) &&
        taxId &&
        'vatResponsible',
      checkIsCountryUkraine(isoCountryCode) && taxId && 'vatStatus',
      checkIsCountryEgypt(isoCountryCode) && taxId && 'taxExemptions',
    ].filter(Boolean);
  };

  const formFields = {
    purchaserContactDetails: contactSectionsRequiredFields,
    billingContactDetails: contactSectionsRequiredFields,
    technicalContactDetails: contactSectionsRequiredFields,
    billingOrganisationDetails: organisationSectionsRequiredFields(
      cart.billingOrganisationDetails?.isoCountryCode,
      cart.billingOrganisationDetails?.taxId,
    ),
    technicalOrganisationDetails: organisationSectionsRequiredFields(
      cart.technicalOrganisationDetails?.isoCountryCode,
      cart.technicalOrganisationDetails?.taxId,
    ),
  };

  const errorMessage = t('billing.address.validation.required');

  const validationErrors: Array<{ field: string; errorKey: string }> = [];

  Object.entries(formFields).forEach(([formSection, fields]) => {
    fields.forEach((field) => {
      const value = cart[formSection][field];

      if (
        (!value && !(field === 'vatResponsible' && value === false)) ||
        (field === 'taxExemptions' &&
          !value.find(({ taxType }) => taxType === 'EG_UIN').exemptionNo)
      ) {
        validationErrors.push({
          field: `${formSection}.${field}`,
          errorKey: errorMessage,
        });
      }
    });
  });

  return validationErrors;
};

export default async (
  cart: Partial<HamsCart>,
  clientConfig: AxiosRequestConfig,
  getState: () => State,
) => {
  const requiredFieldErrors = getRequiredFieldsErrors(cart, getState);

  if (requiredFieldErrors && requiredFieldErrors.length > 0) {
    // @ts-ignore
    throw new FormValidationError(requiredFieldErrors);
  }

  try {
    const r = await hamsClient.post(
      `/1.0/public/order/cart/${cart.uuid}/setCartDetails`,
      cart,
      clientConfig,
    );
    return r.data;
  } catch (error) {
    if (error?.response?.status === 400 && error.i18nFieldErrors) {
      throw new FormValidationError(error.i18nFieldErrors);
    } else {
      setInteractionError(CHECKOUT_CONTACTS_SUBMIT_UFO_NAME, {
        errorMessage: 'checkout contacts submit error',
        name: 'checkout contacts submit error',
      });
    }

    // report to sentry
    reportErrorOnly(error);

    throw error;
  }
};
